/* eslint-disable sonarjs/cognitive-complexity */
import { Injectable } from '@angular/core';

import {
  MovableCardsContainerGroupTypeEnum,
  MovableCardsContainerSingleColumnGroup,
  MovableCardsContainerTwoColumnsGroup,
  SimpleDomRect,
} from '../movable-cards-container.view-model';

@Injectable()
export class RearrangeService<T extends string = string> {
  public toggleWideModeOfCard(
    groups: (MovableCardsContainerSingleColumnGroup<T> | MovableCardsContainerTwoColumnsGroup<T>)[],
    virtualRects: Record<string, SimpleDomRect>,
    cardKey: string
  ): (MovableCardsContainerSingleColumnGroup<T> | MovableCardsContainerTwoColumnsGroup<T>)[] {
    let foundGroupIdx!: number;
    let foundGroup!: MovableCardsContainerSingleColumnGroup<T> | MovableCardsContainerTwoColumnsGroup<T>;
    let foundCardIdx!: number;
    let foundColumnIdx!: number;
    groups.some((group, groupIdx) => {
      return group.type === MovableCardsContainerGroupTypeEnum.SingleColumn
        ? group.cards.some((card, cardIdx) => {
            if (card.key === cardKey) {
              foundGroupIdx = groupIdx;
              foundGroup = group;
              foundCardIdx = cardIdx;
              foundColumnIdx = 0;
              return true;
            } else {
              return false;
            }
          })
        : group.leftCards.some((card, cardIdx) => {
            if (card.key === cardKey) {
              foundGroupIdx = groupIdx;
              foundGroup = group;
              foundCardIdx = cardIdx;
              foundColumnIdx = 0;
              return true;
            } else {
              return false;
            }
          }) ||
            group.rightCards.some((card, cardIdx) => {
              if (card.key === cardKey) {
                foundGroupIdx = groupIdx;
                foundGroup = group;
                foundCardIdx = cardIdx;
                foundColumnIdx = 1;
                return true;
              } else {
                return false;
              }
            });
    });

    return foundGroup.type === MovableCardsContainerGroupTypeEnum.SingleColumn
      ? this.changeSingleColumnCardToTwoColumnsCard(groups, foundGroupIdx, foundCardIdx)
      : this.changeTwoColumnsCardToSingleColumnCard(groups, foundGroupIdx, foundColumnIdx, foundCardIdx, virtualRects);

    return groups;
  }

  public insertTop(
    groups: (MovableCardsContainerSingleColumnGroup<T> | MovableCardsContainerTwoColumnsGroup<T>)[],
    virtualRects: Record<string, SimpleDomRect>,
    draggedCardKey: string,
    draggedCardIdx: number,
    draggedCardColumnIdx: number,
    draggedGroupIdx: number,
    dragZoneOwnerCardKey: string | null,
    targetAreaCardIdx: number | null,
    targetCardColumnIdx: number,
    targetGroupIdx: number
  ): (MovableCardsContainerSingleColumnGroup<T> | MovableCardsContainerTwoColumnsGroup<T>)[] {
    if (draggedCardKey === dragZoneOwnerCardKey) {
      return groups;
    }

    return draggedGroupIdx === targetGroupIdx
      ? this.insertTopInSameGroup(groups, draggedCardIdx, draggedCardColumnIdx, draggedGroupIdx, targetAreaCardIdx, targetCardColumnIdx)
      : this.insertTopInDifferentGroup(
          groups,
          virtualRects,
          draggedCardIdx,
          draggedCardColumnIdx,
          draggedGroupIdx,
          targetGroupIdx,
          targetAreaCardIdx,
          targetCardColumnIdx
        );
  }

  public insertBottom(
    groups: (MovableCardsContainerSingleColumnGroup<T> | MovableCardsContainerTwoColumnsGroup<T>)[],
    virtualRects: Record<string, SimpleDomRect>,
    draggedCardKey: string,
    draggedCardIdx: number,
    draggedCardColumnIdx: number,
    draggedGroupIdx: number,
    dragZoneOwnerCardKey: string | null,
    targetAreaCardIdx: number | null,
    targetCardColumnIdx: number,
    targetGroupIdx: number
  ): (MovableCardsContainerSingleColumnGroup<T> | MovableCardsContainerTwoColumnsGroup<T>)[] {
    if (draggedCardKey === dragZoneOwnerCardKey) {
      return groups;
    }

    const newGroups = groups;

    return draggedGroupIdx === targetGroupIdx
      ? this.insertBottomInSameGroup(
          newGroups,
          draggedCardIdx,
          draggedCardColumnIdx,
          draggedGroupIdx,
          targetAreaCardIdx,
          targetCardColumnIdx
        )
      : this.insertBottomInDifferentGroup(
          newGroups,
          virtualRects,
          draggedCardIdx,
          draggedCardColumnIdx,
          draggedGroupIdx,
          targetGroupIdx,
          targetAreaCardIdx,
          targetCardColumnIdx
        );
  }

  private insertTopInSameGroup(
    groups: (MovableCardsContainerSingleColumnGroup<T> | MovableCardsContainerTwoColumnsGroup<T>)[],
    draggedCardIdx: number,
    draggedCardColumnIdx: number,
    groupIdx: number,
    targetAreaCardIdx: number | null,
    targetCardColumnIdx: number
  ): (MovableCardsContainerSingleColumnGroup<T> | MovableCardsContainerTwoColumnsGroup<T>)[] {
    if (draggedCardIdx === targetAreaCardIdx && draggedCardColumnIdx === targetCardColumnIdx) {
      return groups;
    }

    return groups.map((group, idx) => {
      if (idx === groupIdx) {
        return group.type === MovableCardsContainerGroupTypeEnum.SingleColumn
          ? this.insertTopInSameSingleColumnGroup(group, draggedCardIdx, targetAreaCardIdx!)
          : this.insertTopInSameTwoColumnsGroup(group, draggedCardIdx, draggedCardColumnIdx, targetAreaCardIdx, targetCardColumnIdx);
      } else {
        return group;
      }
    });
  }

  private insertBottomInSameGroup(
    groups: (MovableCardsContainerSingleColumnGroup<T> | MovableCardsContainerTwoColumnsGroup<T>)[],
    draggedCardIdx: number,
    draggedCardColumnIdx: number,
    groupIdx: number,
    targetAreaCardIdx: number | null,
    targetCardColumnIdx: number
  ): (MovableCardsContainerSingleColumnGroup<T> | MovableCardsContainerTwoColumnsGroup<T>)[] {
    if (draggedCardIdx === targetAreaCardIdx && draggedCardColumnIdx === targetCardColumnIdx) {
      return groups;
    }

    return groups.map((group, idx) => {
      if (idx === groupIdx) {
        return group.type === MovableCardsContainerGroupTypeEnum.SingleColumn
          ? this.insertBottomInSameSingleColumnGroup(group, draggedCardIdx, targetAreaCardIdx!)
          : this.insertBottomInSameTwoColumnsGroup(group, draggedCardIdx, draggedCardColumnIdx, targetAreaCardIdx, targetCardColumnIdx);
      } else {
        return group;
      }
    });
  }

  private insertTopInSameSingleColumnGroup(
    group: MovableCardsContainerSingleColumnGroup<T>,
    draggedCardIdx: number,
    targetAreaCardIdx: number
  ): MovableCardsContainerSingleColumnGroup<T> {
    const realTargetCardIdx = draggedCardIdx < targetAreaCardIdx ? targetAreaCardIdx - 1 : targetAreaCardIdx;

    if (realTargetCardIdx === draggedCardIdx) {
      return group;
    }

    const newCards = [...group.cards];
    newCards.splice(realTargetCardIdx, 0, newCards.splice(draggedCardIdx, 1)[0]);

    return {
      type: MovableCardsContainerGroupTypeEnum.SingleColumn,
      cards: newCards,
    };
  }

  private insertBottomInSameSingleColumnGroup(
    group: MovableCardsContainerSingleColumnGroup<T>,
    draggedCardIdx: number,
    targetAreaCardIdx: number
  ): MovableCardsContainerSingleColumnGroup<T> {
    const realTargetCardIdx = draggedCardIdx > targetAreaCardIdx ? targetAreaCardIdx + 1 : targetAreaCardIdx;

    if (realTargetCardIdx === draggedCardIdx) {
      return group;
    }

    const newCards = [...group.cards];
    newCards.splice(realTargetCardIdx, 0, newCards.splice(draggedCardIdx, 1)[0]);

    return {
      type: MovableCardsContainerGroupTypeEnum.SingleColumn,
      cards: newCards,
    };
  }

  private insertTopInSameTwoColumnsGroup(
    group: MovableCardsContainerTwoColumnsGroup<T>,
    draggedCardIdx: number,
    draggedCardColumnIdx: number,
    targetAreaCardIdx: number | null,
    targetCardColumnIdx: number
  ): MovableCardsContainerTwoColumnsGroup<T> {
    return draggedCardColumnIdx === targetCardColumnIdx
      ? this.insertTopInSameTwoColumnsGroupSameColumn(group, draggedCardIdx, draggedCardColumnIdx, targetAreaCardIdx)
      : this.insertTopInSameTwoColumnsGroupDifferentColumn(group, draggedCardIdx, draggedCardColumnIdx, targetAreaCardIdx);
  }

  private insertBottomInSameTwoColumnsGroup(
    group: MovableCardsContainerTwoColumnsGroup<T>,
    draggedCardIdx: number,
    draggedCardColumnIdx: number,
    targetAreaCardIdx: number | null,
    targetCardColumnIdx: number
  ): MovableCardsContainerTwoColumnsGroup<T> {
    return draggedCardColumnIdx === targetCardColumnIdx
      ? this.insertBottomInSameTwoColumnsGroupSameColumn(group, draggedCardIdx, draggedCardColumnIdx, targetAreaCardIdx)
      : this.insertBottomInSameTwoColumnsGroupDifferentColumn(group, draggedCardIdx, draggedCardColumnIdx, targetAreaCardIdx);
  }

  private insertTopInSameTwoColumnsGroupDifferentColumn(
    group: MovableCardsContainerTwoColumnsGroup<T>,
    draggedCardIdx: number,
    draggedCardColumnIdx: number,
    targetAreaCardIdx: number | null
  ): MovableCardsContainerTwoColumnsGroup<T> {
    if (draggedCardColumnIdx === 0) {
      const newRightCards = [...group.rightCards];
      if (targetAreaCardIdx === null) {
        newRightCards.push(group.leftCards[draggedCardIdx]);
      } else {
        newRightCards.splice(targetAreaCardIdx, 0, group.leftCards[draggedCardIdx]);
      }

      const newLeftCards = group.leftCards.filter((_, idx) => idx !== draggedCardIdx);

      return {
        ...group,
        leftCards: newLeftCards,
        rightCards: newRightCards,
      };
    } else {
      const newLeftCards = [...group.leftCards];
      if (targetAreaCardIdx === null) {
        newLeftCards.push(group.rightCards[draggedCardIdx]);
      } else {
        newLeftCards.splice(targetAreaCardIdx, 0, group.rightCards[draggedCardIdx]);
      }

      const newRightCards = group.rightCards.filter((_, idx) => idx !== draggedCardIdx);

      return {
        ...group,
        leftCards: newLeftCards,
        rightCards: newRightCards,
      };
    }
  }

  private insertBottomInSameTwoColumnsGroupDifferentColumn(
    group: MovableCardsContainerTwoColumnsGroup<T>,
    draggedCardIdx: number,
    draggedCardColumnIdx: number,
    targetAreaCardIdx: number | null
  ): MovableCardsContainerTwoColumnsGroup<T> {
    if (draggedCardColumnIdx === 0) {
      const newRightCards = [...group.rightCards];
      if (targetAreaCardIdx === null) {
        newRightCards.push(group.leftCards[draggedCardIdx]);
      } else {
        newRightCards.splice(targetAreaCardIdx + 1, 0, group.leftCards[draggedCardIdx]);
      }

      const newLeftCards = group.leftCards.filter((_, idx) => idx !== draggedCardIdx);

      return {
        ...group,
        leftCards: newLeftCards,
        rightCards: newRightCards,
      };
    } else {
      const newLeftCards = [...group.leftCards];
      if (targetAreaCardIdx === null) {
        newLeftCards.push(group.rightCards[draggedCardIdx]);
      } else {
        newLeftCards.splice(targetAreaCardIdx + 1, 0, group.rightCards[draggedCardIdx]);
      }

      const newRightCards = group.rightCards.filter((_, idx) => idx !== draggedCardIdx);

      return {
        ...group,
        leftCards: newLeftCards,
        rightCards: newRightCards,
      };
    }
  }

  private insertTopInSameTwoColumnsGroupSameColumn(
    group: MovableCardsContainerTwoColumnsGroup<T>,
    draggedCardIdx: number,
    draggedCardColumnIdx: number,
    targetAreaCardIdx: number | null
  ): MovableCardsContainerTwoColumnsGroup<T> {
    if (draggedCardColumnIdx === 0) {
      let realTargetCardIdx = targetAreaCardIdx ?? group.leftCards.length - 1;

      if (draggedCardIdx < realTargetCardIdx) {
        realTargetCardIdx--;
      }

      if (draggedCardIdx === realTargetCardIdx) {
        return group;
      }

      const newLeftCards = [...group.leftCards];
      newLeftCards.splice(realTargetCardIdx!, 0, newLeftCards.splice(draggedCardIdx, 1)[0]);

      return {
        ...group,
        leftCards: newLeftCards,
      };
    } else {
      let realTargetCardIdx = targetAreaCardIdx ?? group.rightCards.length - 1;

      if (draggedCardIdx < realTargetCardIdx) {
        realTargetCardIdx--;
      }

      if (draggedCardIdx === realTargetCardIdx) {
        return group;
      }

      const newRightCards = [...group.rightCards];
      newRightCards.splice(realTargetCardIdx, 0, newRightCards.splice(draggedCardIdx, 1)[0]);

      return {
        ...group,
        rightCards: newRightCards,
      };
    }
  }

  private insertBottomInSameTwoColumnsGroupSameColumn(
    group: MovableCardsContainerTwoColumnsGroup<T>,
    draggedCardIdx: number,
    draggedCardColumnIdx: number,
    targetAreaCardIdx: number | null
  ): MovableCardsContainerTwoColumnsGroup<T> {
    if (draggedCardColumnIdx === 0) {
      let realTargetCardIdx = targetAreaCardIdx ?? group.leftCards.length - 1;

      if (draggedCardIdx > realTargetCardIdx) {
        realTargetCardIdx++;
      }

      if (draggedCardIdx === realTargetCardIdx) {
        return group;
      }

      const newLeftCards = [...group.leftCards];
      newLeftCards.splice(realTargetCardIdx!, 0, newLeftCards.splice(draggedCardIdx, 1)[0]);

      return {
        ...group,
        leftCards: newLeftCards,
      };
    } else {
      let realTargetCardIdx = targetAreaCardIdx ?? group.rightCards.length - 1;

      if (draggedCardIdx > realTargetCardIdx) {
        realTargetCardIdx++;
      }

      if (draggedCardIdx === realTargetCardIdx) {
        return group;
      }

      const newRightCards = [...group.rightCards];
      newRightCards.splice(realTargetCardIdx, 0, newRightCards.splice(draggedCardIdx, 1)[0]);

      return {
        ...group,
        rightCards: newRightCards,
      };
    }
  }

  private insertTopInDifferentGroup(
    groups: (MovableCardsContainerSingleColumnGroup<T> | MovableCardsContainerTwoColumnsGroup<T>)[],
    virtualRects: Record<string, SimpleDomRect>,
    draggedCardIdx: number,
    draggedCardColumnIdx: number,
    draggedGroupIdx: number,
    targetGroupIdx: number,
    targetAreaCardIdx: number | null,
    targetCardColumnIdx: number
  ): (MovableCardsContainerSingleColumnGroup<T> | MovableCardsContainerTwoColumnsGroup<T>)[] {
    const draggedGroupType = groups[draggedGroupIdx].type;
    const targetGroupType = groups[targetGroupIdx].type;

    return draggedGroupType === targetGroupType
      ? this.insertTopInDifferentGroupSameType(
          groups,
          draggedCardIdx,
          draggedCardColumnIdx,
          draggedGroupIdx,
          targetGroupIdx,
          targetAreaCardIdx,
          targetCardColumnIdx
        )
      : this.insertTopInDifferentGroupDifferentType(
          groups,
          virtualRects,
          draggedCardIdx,
          draggedCardColumnIdx,
          draggedGroupIdx,
          targetGroupIdx,
          targetAreaCardIdx,
          targetCardColumnIdx
        );
  }

  private insertBottomInDifferentGroup(
    groups: (MovableCardsContainerSingleColumnGroup<T> | MovableCardsContainerTwoColumnsGroup<T>)[],
    virtualRects: Record<string, SimpleDomRect>,
    draggedCardIdx: number,
    draggedCardColumnIdx: number,
    draggedGroupIdx: number,
    targetGroupIdx: number,
    targetAreaCardIdx: number | null,
    targetCardColumnIdx: number
  ): (MovableCardsContainerSingleColumnGroup<T> | MovableCardsContainerTwoColumnsGroup<T>)[] {
    const draggedGroupType = groups[draggedGroupIdx].type;
    const targetGroupType = groups[targetGroupIdx].type;

    return draggedGroupType === targetGroupType
      ? this.insertBottomInDifferentGroupSameType(
          groups,
          draggedCardIdx,
          draggedCardColumnIdx,
          draggedGroupIdx,
          targetGroupIdx,
          targetAreaCardIdx,
          targetCardColumnIdx
        )
      : this.insertBottomInDifferentGroupDifferentType(
          groups,
          virtualRects,
          draggedCardIdx,
          draggedCardColumnIdx,
          draggedGroupIdx,
          targetGroupIdx,
          targetAreaCardIdx,
          targetCardColumnIdx
        );
  }

  private insertTopInDifferentGroupSameType(
    groups: (MovableCardsContainerSingleColumnGroup<T> | MovableCardsContainerTwoColumnsGroup<T>)[],
    draggedCardIdx: number,
    draggedCardColumnIdx: number,
    draggedGroupIdx: number,
    targetGroupIdx: number,
    targetAreaCardIdx: number | null,
    targetCardColumnIdx: number
  ): (MovableCardsContainerSingleColumnGroup<T> | MovableCardsContainerTwoColumnsGroup<T>)[] {
    const groupType = groups[draggedGroupIdx].type;

    return groupType === MovableCardsContainerGroupTypeEnum.SingleColumn
      ? this.insertTopInDifferentGroupSameTypeSingleColumn(groups, draggedCardIdx, draggedGroupIdx, targetGroupIdx, targetAreaCardIdx!)
      : this.insertTopInDifferentGroupSameTypeTwoColumns(
          groups,
          draggedCardIdx,
          draggedGroupIdx,
          draggedCardColumnIdx,
          targetGroupIdx,
          targetAreaCardIdx,
          targetCardColumnIdx
        );
  }

  private insertBottomInDifferentGroupSameType(
    groups: (MovableCardsContainerSingleColumnGroup<T> | MovableCardsContainerTwoColumnsGroup<T>)[],
    draggedCardIdx: number,
    draggedCardColumnIdx: number,
    draggedGroupIdx: number,
    targetGroupIdx: number,
    targetAreaCardIdx: number | null,
    targetCardColumnIdx: number
  ): (MovableCardsContainerSingleColumnGroup<T> | MovableCardsContainerTwoColumnsGroup<T>)[] {
    const groupType = groups[draggedGroupIdx].type;

    return groupType === MovableCardsContainerGroupTypeEnum.SingleColumn
      ? this.insertBottomInDifferentGroupSameTypeSingleColumn(groups, draggedCardIdx, draggedGroupIdx, targetGroupIdx, targetAreaCardIdx!)
      : this.insertBottomInDifferentGroupSameTypeTwoColumns(
          groups,
          draggedCardIdx,
          draggedGroupIdx,
          draggedCardColumnIdx,
          targetGroupIdx,
          targetAreaCardIdx,
          targetCardColumnIdx
        );
  }

  private insertTopInDifferentGroupSameTypeSingleColumn(
    groups: (MovableCardsContainerSingleColumnGroup<T> | MovableCardsContainerTwoColumnsGroup<T>)[],
    draggedCardIdx: number,
    draggedGroupIdx: number,
    targetGroupIdx: number,
    targetAreaCardIdx: number
  ): (MovableCardsContainerSingleColumnGroup<T> | MovableCardsContainerTwoColumnsGroup<T>)[] {
    const newGroups = [...groups];
    const draggedGroup = newGroups[draggedGroupIdx] as MovableCardsContainerSingleColumnGroup<T>;
    const targetGroup = newGroups[targetGroupIdx] as MovableCardsContainerSingleColumnGroup<T>;

    const newDraggedGroup = {
      ...draggedGroup,
      cards: draggedGroup.cards.filter((_, idx) => idx !== draggedCardIdx),
    };

    const newTargetGroupCards = [...targetGroup.cards];
    newTargetGroupCards.splice(targetAreaCardIdx, 0, draggedGroup.cards[draggedCardIdx]);

    const newTargetGroup = {
      ...targetGroup,
      cards: newTargetGroupCards,
    };

    newGroups[draggedGroupIdx] = newDraggedGroup;
    newGroups[targetGroupIdx] = newTargetGroup;

    return this.mergeConsecutiveGroups(newGroups);
  }

  private insertBottomInDifferentGroupSameTypeSingleColumn(
    groups: (MovableCardsContainerSingleColumnGroup<T> | MovableCardsContainerTwoColumnsGroup<T>)[],
    draggedCardIdx: number,
    draggedGroupIdx: number,
    targetGroupIdx: number,
    targetAreaCardIdx: number
  ): (MovableCardsContainerSingleColumnGroup<T> | MovableCardsContainerTwoColumnsGroup<T>)[] {
    const newGroups = [...groups];
    const draggedGroup = newGroups[draggedGroupIdx] as MovableCardsContainerSingleColumnGroup<T>;
    const targetGroup = newGroups[targetGroupIdx] as MovableCardsContainerSingleColumnGroup<T>;

    const newDraggedGroup = {
      ...draggedGroup,
      cards: draggedGroup.cards.filter((_, idx) => idx !== draggedCardIdx),
    };

    const newTargetGroupCards = [...targetGroup.cards];
    newTargetGroupCards.splice(targetAreaCardIdx + 1, 0, draggedGroup.cards[draggedCardIdx]);

    const newTargetGroup = {
      ...targetGroup,
      cards: newTargetGroupCards,
    };

    newGroups[draggedGroupIdx] = newDraggedGroup;
    newGroups[targetGroupIdx] = newTargetGroup;

    return this.mergeConsecutiveGroups(newGroups);
  }

  private insertTopInDifferentGroupSameTypeTwoColumns(
    groups: (MovableCardsContainerSingleColumnGroup<T> | MovableCardsContainerTwoColumnsGroup<T>)[],
    draggedCardIdx: number,
    draggedGroupIdx: number,
    draggedColumnIdx: number,
    targetGroupIdx: number,
    targetAreaCardIdx: number | null,
    targetColumnIdx: number | null
  ): (MovableCardsContainerSingleColumnGroup<T> | MovableCardsContainerTwoColumnsGroup<T>)[] {
    const newGroups = [...groups];
    const draggedGroup = newGroups[draggedGroupIdx] as MovableCardsContainerTwoColumnsGroup<T>;
    const targetGroup = newGroups[targetGroupIdx] as MovableCardsContainerTwoColumnsGroup<T>;
    const draggedCard = draggedColumnIdx === 0 ? draggedGroup.leftCards[draggedCardIdx] : draggedGroup.rightCards[draggedCardIdx];

    const newDraggedGroup: MovableCardsContainerTwoColumnsGroup<T> = {
      ...draggedGroup,
      leftCards: draggedColumnIdx === 0 ? draggedGroup.leftCards.filter((_, idx) => idx !== draggedCardIdx) : draggedGroup.leftCards,
      rightCards: draggedColumnIdx === 1 ? draggedGroup.rightCards.filter((_, idx) => idx !== draggedCardIdx) : draggedGroup.rightCards,
    };

    let newTargetGroup!: MovableCardsContainerTwoColumnsGroup<T>;

    if (targetColumnIdx === 0) {
      const newTargetGroupLeftCards = [...targetGroup.leftCards];

      if (targetAreaCardIdx === null) {
        newTargetGroupLeftCards.push(draggedCard);
      } else {
        newTargetGroupLeftCards.splice(targetAreaCardIdx, 0, draggedCard);
      }

      newTargetGroup = {
        ...targetGroup,
        leftCards: newTargetGroupLeftCards,
      };
    } else {
      const newTargetGroupRightCards = [...targetGroup.rightCards];

      if (targetAreaCardIdx === null) {
        newTargetGroupRightCards.push(draggedCard);
      } else {
        newTargetGroupRightCards.splice(targetAreaCardIdx, 0, draggedCard);
      }

      newTargetGroup = {
        ...targetGroup,
        rightCards: newTargetGroupRightCards,
      };
    }

    newGroups[draggedGroupIdx] = newDraggedGroup;
    newGroups[targetGroupIdx] = newTargetGroup;

    return this.mergeConsecutiveGroups(newGroups);
  }

  private insertBottomInDifferentGroupSameTypeTwoColumns(
    groups: (MovableCardsContainerSingleColumnGroup<T> | MovableCardsContainerTwoColumnsGroup<T>)[],
    draggedCardIdx: number,
    draggedGroupIdx: number,
    draggedColumnIdx: number,
    targetGroupIdx: number,
    targetAreaCardIdx: number | null,
    targetColumnIdx: number | null
  ): (MovableCardsContainerSingleColumnGroup<T> | MovableCardsContainerTwoColumnsGroup<T>)[] {
    const newGroups = [...groups];
    const draggedGroup = newGroups[draggedGroupIdx] as MovableCardsContainerTwoColumnsGroup<T>;
    const targetGroup = newGroups[targetGroupIdx] as MovableCardsContainerTwoColumnsGroup<T>;
    const draggedCard = draggedColumnIdx === 0 ? draggedGroup.leftCards[draggedCardIdx] : draggedGroup.rightCards[draggedCardIdx];

    const newDraggedGroup: MovableCardsContainerTwoColumnsGroup<T> = {
      ...draggedGroup,
      leftCards: draggedColumnIdx === 0 ? draggedGroup.leftCards.filter((_, idx) => idx !== draggedCardIdx) : draggedGroup.leftCards,
      rightCards: draggedColumnIdx === 1 ? draggedGroup.rightCards.filter((_, idx) => idx !== draggedCardIdx) : draggedGroup.rightCards,
    };

    let newTargetGroup!: MovableCardsContainerTwoColumnsGroup<T>;

    if (targetColumnIdx === 0) {
      const newTargetGroupLeftCards = [...targetGroup.leftCards];

      if (targetAreaCardIdx === null) {
        newTargetGroupLeftCards.push(draggedCard);
      } else {
        newTargetGroupLeftCards.splice(targetAreaCardIdx + 1, 0, draggedCard);
      }

      newTargetGroup = {
        ...targetGroup,
        leftCards: newTargetGroupLeftCards,
      };
    } else {
      const newTargetGroupRightCards = [...targetGroup.rightCards];

      if (targetAreaCardIdx === null) {
        newTargetGroupRightCards.push(draggedCard);
      } else {
        newTargetGroupRightCards.splice(targetAreaCardIdx + 1, 0, draggedCard);
      }

      newTargetGroup = {
        ...targetGroup,
        rightCards: newTargetGroupRightCards,
      };
    }

    newGroups[draggedGroupIdx] = newDraggedGroup;
    newGroups[targetGroupIdx] = newTargetGroup;

    return this.mergeConsecutiveGroups(newGroups);
  }

  private mergeConsecutiveGroups(
    groups: (MovableCardsContainerSingleColumnGroup<T> | MovableCardsContainerTwoColumnsGroup<T>)[]
  ): (MovableCardsContainerSingleColumnGroup<T> | MovableCardsContainerTwoColumnsGroup<T>)[] {
    const newGroups: (MovableCardsContainerSingleColumnGroup<T> | MovableCardsContainerTwoColumnsGroup<T>)[] = [];

    groups.forEach((group) => {
      if (this.isGroupEmpty(group)) {
        return;
      }

      if (newGroups.length === 0) {
        newGroups.push(group);
        return;
      }

      const lastGroup = newGroups[newGroups.length - 1];
      if (lastGroup.type === group.type) {
        if (group.type === MovableCardsContainerGroupTypeEnum.SingleColumn) {
          newGroups[newGroups.length - 1] = this.mergeConsecutiveSingleColumnGroups(
            lastGroup as MovableCardsContainerSingleColumnGroup<T>,
            group as MovableCardsContainerSingleColumnGroup<T>
          );
        } else {
          newGroups[newGroups.length - 1] = this.mergeConsecutiveTwoColumnsGroups(
            lastGroup as MovableCardsContainerTwoColumnsGroup<T>,
            group as MovableCardsContainerTwoColumnsGroup<T>
          );
        }
      } else {
        newGroups.push(group);
      }
    });

    return newGroups;
  }

  private mergeConsecutiveSingleColumnGroups(
    targetGroup: MovableCardsContainerSingleColumnGroup<T>,
    groupToMerge: MovableCardsContainerSingleColumnGroup<T>
  ): MovableCardsContainerSingleColumnGroup<T> {
    const newTargetGroup: MovableCardsContainerSingleColumnGroup<T> = {
      ...targetGroup,
      cards: [...targetGroup.cards, ...groupToMerge.cards],
    };

    return newTargetGroup;
  }

  private insertTopInDifferentGroupDifferentType(
    groups: (MovableCardsContainerSingleColumnGroup<T> | MovableCardsContainerTwoColumnsGroup<T>)[],
    virtualRects: Record<string, SimpleDomRect>,
    draggedCardIdx: number,
    draggedCardColumnIdx: number,
    draggedGroupIdx: number,
    targetGroupIdx: number,
    targetAreaCardIdx: number | null,
    targetCardColumnIdx: number
  ): (MovableCardsContainerSingleColumnGroup<T> | MovableCardsContainerTwoColumnsGroup<T>)[] {
    const draggedGroupType = groups[draggedGroupIdx].type;

    return draggedGroupType === MovableCardsContainerGroupTypeEnum.TwoColumns
      ? this.insertTopFromTwoColumnsToSingleColumn(
          groups,
          draggedCardIdx,
          draggedCardColumnIdx,
          draggedGroupIdx,
          targetGroupIdx,
          targetAreaCardIdx!,
          targetCardColumnIdx
        )
      : this.insertTopFromSingleColumnToTwoColumns(
          groups,
          virtualRects,
          draggedCardIdx,
          draggedGroupIdx,
          targetGroupIdx,
          targetAreaCardIdx!,
          targetCardColumnIdx
        );
  }

  private insertBottomInDifferentGroupDifferentType(
    groups: (MovableCardsContainerSingleColumnGroup<T> | MovableCardsContainerTwoColumnsGroup<T>)[],
    virtualRects: Record<string, SimpleDomRect>,
    draggedCardIdx: number,
    draggedCardColumnIdx: number,
    draggedGroupIdx: number,
    targetGroupIdx: number,
    targetAreaCardIdx: number | null,
    targetCardColumnIdx: number
  ): (MovableCardsContainerSingleColumnGroup<T> | MovableCardsContainerTwoColumnsGroup<T>)[] {
    const draggedGroupType = groups[draggedGroupIdx].type;

    return draggedGroupType === MovableCardsContainerGroupTypeEnum.TwoColumns
      ? this.insertBottomFromTwoColumnsToSingleColumn(
          groups,
          draggedCardIdx,
          draggedCardColumnIdx,
          draggedGroupIdx,
          targetGroupIdx,
          targetAreaCardIdx!,
          targetCardColumnIdx
        )
      : this.insertBottomFromSingleColumnToTwoColumns(
          groups,
          virtualRects,
          draggedCardIdx,
          draggedGroupIdx,
          targetGroupIdx,
          targetAreaCardIdx,
          targetCardColumnIdx
        );
  }

  private insertTopFromTwoColumnsToSingleColumn(
    groups: (MovableCardsContainerSingleColumnGroup<T> | MovableCardsContainerTwoColumnsGroup<T>)[],
    draggedCardIdx: number,
    draggedCardColumnIdx: number,
    draggedGroupIdx: number,
    targetGroupIdx: number,
    targetAreaCardIdx: number,
    targetCardColumnIdx: number
  ): (MovableCardsContainerSingleColumnGroup<T> | MovableCardsContainerTwoColumnsGroup<T>)[] {
    const draggedGroup = groups[draggedGroupIdx] as MovableCardsContainerTwoColumnsGroup<T>;
    const draggedCard = draggedCardColumnIdx === 0 ? draggedGroup.leftCards[draggedCardIdx] : draggedGroup.rightCards[draggedCardIdx];

    let newDraggedGroup = draggedGroup;
    const newLeftCards =
      draggedCardColumnIdx === 0 ? newDraggedGroup.leftCards.filter((_, cardIdx) => cardIdx !== draggedCardIdx) : newDraggedGroup.leftCards;
    const newRightCards =
      draggedCardColumnIdx === 1
        ? newDraggedGroup.rightCards.filter((_, cardIdx) => cardIdx !== draggedCardIdx)
        : newDraggedGroup.rightCards;
    newDraggedGroup = {
      ...newDraggedGroup,
      leftCards: newLeftCards,
      rightCards: newRightCards,
    };

    const newGroups = [...groups];
    newGroups[draggedGroupIdx] = newDraggedGroup;

    const targetGroup = newGroups[targetGroupIdx] as MovableCardsContainerSingleColumnGroup<T>;

    const newGroup: MovableCardsContainerTwoColumnsGroup<T> = {
      type: MovableCardsContainerGroupTypeEnum.TwoColumns,
      leftCards: targetCardColumnIdx === 0 ? [draggedCard] : [],
      rightCards: targetCardColumnIdx === 1 ? [draggedCard] : [],
    };

    if (targetAreaCardIdx === 0) {
      newGroups.splice(targetGroupIdx, 0, newGroup);
    } else {
      const cutOffSingleColumnCards = targetGroup.cards.slice(0, targetAreaCardIdx);
      const remainingSingleColumnCards = targetGroup.cards.slice(targetAreaCardIdx);

      newGroups.splice(
        targetGroupIdx,
        1,
        {
          type: MovableCardsContainerGroupTypeEnum.SingleColumn,
          cards: cutOffSingleColumnCards,
        },
        newGroup,
        {
          type: MovableCardsContainerGroupTypeEnum.SingleColumn,
          cards: remainingSingleColumnCards,
        }
      );
    }

    return this.mergeConsecutiveGroups(newGroups);
  }

  private insertTopFromSingleColumnToTwoColumns(
    groups: (MovableCardsContainerSingleColumnGroup<T> | MovableCardsContainerTwoColumnsGroup<T>)[],
    virtualRects: Record<string, SimpleDomRect>,
    draggedCardIdx: number,
    draggedGroupIdx: number,
    targetGroupIdx: number,
    targetAreaCardIdx: number,
    targetCardColumnIdx: number
  ): (MovableCardsContainerSingleColumnGroup<T> | MovableCardsContainerTwoColumnsGroup<T>)[] {
    const draggedGroup = groups[draggedGroupIdx] as MovableCardsContainerSingleColumnGroup<T>;
    const draggedCard = draggedGroup.cards[draggedCardIdx];

    let newDraggedGroup = draggedGroup;
    const newCards = newDraggedGroup.cards.filter((_, cardIdx) => cardIdx !== draggedCardIdx);
    newDraggedGroup = {
      ...newDraggedGroup,
      cards: newCards,
    };

    const newGroups = [...groups];
    newGroups[draggedGroupIdx] = newDraggedGroup;

    const targetGroup = newGroups[targetGroupIdx] as MovableCardsContainerTwoColumnsGroup<T>;

    const newGroup: MovableCardsContainerSingleColumnGroup<T> = {
      type: MovableCardsContainerGroupTypeEnum.SingleColumn,
      cards: [draggedCard],
    };

    if (targetAreaCardIdx === 0) {
      newGroups.splice(targetGroupIdx, 0, newGroup);
    } else {
      if (targetCardColumnIdx === 0) {
        const cutOffLeftCards = targetGroup.leftCards.slice(0, targetAreaCardIdx);
        const remainingLeftCards = targetGroup.leftCards.slice(targetAreaCardIdx);

        const lastCutOffLeftCardKey = cutOffLeftCards[cutOffLeftCards.length - 1].key;
        const breakpoint = virtualRects[lastCutOffLeftCardKey].top + virtualRects[lastCutOffLeftCardKey].height;

        const firstNotFittingRightCardIdx = targetGroup.rightCards.findIndex((rightCard) => {
          const bottomPointOfCard = virtualRects[rightCard.key].top + virtualRects[rightCard.key].height;

          return bottomPointOfCard > breakpoint;
        });

        const cutOffRightCards =
          firstNotFittingRightCardIdx === -1 ? targetGroup.rightCards : targetGroup.rightCards.slice(0, firstNotFittingRightCardIdx);
        const remainingRightCards = firstNotFittingRightCardIdx === -1 ? [] : targetGroup.rightCards.slice(firstNotFittingRightCardIdx);

        newGroups.splice(
          targetGroupIdx,
          1,
          {
            type: MovableCardsContainerGroupTypeEnum.TwoColumns,
            leftCards: cutOffLeftCards,
            rightCards: cutOffRightCards,
          },
          newGroup,
          {
            type: MovableCardsContainerGroupTypeEnum.TwoColumns,
            leftCards: remainingLeftCards,
            rightCards: remainingRightCards,
          }
        );
      } else {
        const cutOffRightCards = targetGroup.rightCards.slice(0, targetAreaCardIdx);
        const remainingRightCards = targetGroup.rightCards.slice(targetAreaCardIdx);

        const lastCutOffRightCardKey = cutOffRightCards[cutOffRightCards.length - 1].key;
        const breakpoint = virtualRects[lastCutOffRightCardKey].top + virtualRects[lastCutOffRightCardKey].height;

        const firstNotFittingLeftCardIdx = targetGroup.leftCards.findIndex((leftCard) => {
          const bottomPointOfCard = virtualRects[leftCard.key].top + virtualRects[leftCard.key].height;

          return bottomPointOfCard > breakpoint;
        });

        const cutOffLeftCards =
          firstNotFittingLeftCardIdx === -1 ? targetGroup.leftCards : targetGroup.leftCards.slice(0, firstNotFittingLeftCardIdx);
        const remainingLeftCards = firstNotFittingLeftCardIdx === -1 ? [] : targetGroup.leftCards.slice(firstNotFittingLeftCardIdx);

        newGroups.splice(
          targetGroupIdx,
          1,
          {
            type: MovableCardsContainerGroupTypeEnum.TwoColumns,
            leftCards: cutOffLeftCards,
            rightCards: cutOffRightCards,
          },
          newGroup,
          {
            type: MovableCardsContainerGroupTypeEnum.TwoColumns,
            leftCards: remainingLeftCards,
            rightCards: remainingRightCards,
          }
        );
      }
    }

    return this.mergeConsecutiveGroups(newGroups);
  }

  private insertBottomFromTwoColumnsToSingleColumn(
    groups: (MovableCardsContainerSingleColumnGroup<T> | MovableCardsContainerTwoColumnsGroup<T>)[],
    draggedCardIdx: number,
    draggedCardColumnIdx: number,
    draggedGroupIdx: number,
    targetGroupIdx: number,
    targetAreaCardIdx: number,
    targetCardColumnIdx: number
  ): (MovableCardsContainerSingleColumnGroup<T> | MovableCardsContainerTwoColumnsGroup<T>)[] {
    const draggedGroup = groups[draggedGroupIdx] as MovableCardsContainerTwoColumnsGroup<T>;
    const draggedCard = draggedCardColumnIdx === 0 ? draggedGroup.leftCards[draggedCardIdx] : draggedGroup.rightCards[draggedCardIdx];

    let newDraggedGroup = draggedGroup;
    const newLeftCards =
      draggedCardColumnIdx === 0 ? newDraggedGroup.leftCards.filter((_, cardIdx) => cardIdx !== draggedCardIdx) : newDraggedGroup.leftCards;
    const newRightCards =
      draggedCardColumnIdx === 1
        ? newDraggedGroup.rightCards.filter((_, cardIdx) => cardIdx !== draggedCardIdx)
        : newDraggedGroup.rightCards;
    newDraggedGroup = {
      ...newDraggedGroup,
      leftCards: newLeftCards,
      rightCards: newRightCards,
    };

    const newGroups = [...groups];
    newGroups[draggedGroupIdx] = newDraggedGroup;

    const targetGroup = newGroups[targetGroupIdx] as MovableCardsContainerSingleColumnGroup<T>;

    const newGroup: MovableCardsContainerTwoColumnsGroup<T> = {
      type: MovableCardsContainerGroupTypeEnum.TwoColumns,
      leftCards: targetCardColumnIdx === 0 ? [draggedCard] : [],
      rightCards: targetCardColumnIdx === 1 ? [draggedCard] : [],
    };

    if (targetAreaCardIdx === targetGroup.cards.length - 1) {
      newGroups.splice(targetGroupIdx + 1, 0, newGroup);
    } else {
      const cutOffSingleColumnCards = targetGroup.cards.slice(0, targetAreaCardIdx + 1);
      const remainingSingleColumnCards = targetGroup.cards.slice(targetAreaCardIdx + 1);

      newGroups.splice(
        targetGroupIdx,
        1,
        {
          type: MovableCardsContainerGroupTypeEnum.SingleColumn,
          cards: cutOffSingleColumnCards,
        },
        newGroup,
        {
          type: MovableCardsContainerGroupTypeEnum.SingleColumn,
          cards: remainingSingleColumnCards,
        }
      );
    }

    return this.mergeConsecutiveGroups(newGroups);
  }

  private insertBottomFromSingleColumnToTwoColumns(
    groups: (MovableCardsContainerSingleColumnGroup<T> | MovableCardsContainerTwoColumnsGroup<T>)[],
    virtualRects: Record<string, SimpleDomRect>,
    draggedCardIdx: number,
    draggedGroupIdx: number,
    targetGroupIdx: number,
    targetAreaCardIdx: number | null,
    targetCardColumnIdx: number
  ): (MovableCardsContainerSingleColumnGroup<T> | MovableCardsContainerTwoColumnsGroup<T>)[] {
    const draggedGroup = groups[draggedGroupIdx] as MovableCardsContainerSingleColumnGroup<T>;
    const draggedCard = draggedGroup.cards[draggedCardIdx];

    let newDraggedGroup = draggedGroup;
    const newCards = newDraggedGroup.cards.filter((_, cardIdx) => cardIdx !== draggedCardIdx);
    newDraggedGroup = {
      ...newDraggedGroup,
      cards: newCards,
    };

    const newGroups = [...groups];
    newGroups[draggedGroupIdx] = newDraggedGroup;

    const targetGroup = newGroups[targetGroupIdx] as MovableCardsContainerTwoColumnsGroup<T>;

    const newGroup: MovableCardsContainerSingleColumnGroup<T> = {
      type: MovableCardsContainerGroupTypeEnum.SingleColumn,
      cards: [draggedCard],
    };

    if (targetCardColumnIdx === 0) {
      const targetCardColumnIsEmpty = targetGroup.leftCards.length === 0;
      if (targetCardColumnIsEmpty) {
        return groups;
      }

      const realTargetCardIdx = targetAreaCardIdx ?? targetGroup.leftCards.length - 1;

      if (realTargetCardIdx === targetGroup.leftCards.length - 1) {
        const breakpoint =
          virtualRects[targetGroup.leftCards[realTargetCardIdx].key].top +
          virtualRects[targetGroup.leftCards[realTargetCardIdx].key].height;

        const firstNotFittingRightCardIdx = targetGroup.rightCards.findIndex((rightCard) => {
          const bottomPointOfCard = virtualRects[rightCard.key].top + virtualRects[rightCard.key].height;

          return bottomPointOfCard > breakpoint;
        });

        const cutOffRightCards =
          firstNotFittingRightCardIdx === -1 ? targetGroup.rightCards : targetGroup.rightCards.slice(0, firstNotFittingRightCardIdx);
        const remainingRightCards = firstNotFittingRightCardIdx === -1 ? [] : targetGroup.rightCards.slice(firstNotFittingRightCardIdx);

        newGroups.splice(
          targetGroupIdx,
          1,
          {
            type: MovableCardsContainerGroupTypeEnum.TwoColumns,
            leftCards: targetGroup.leftCards,
            rightCards: cutOffRightCards,
          },
          newGroup,
          {
            type: MovableCardsContainerGroupTypeEnum.TwoColumns,
            leftCards: [],
            rightCards: remainingRightCards,
          }
        );
      } else {
        const cutOffLeftCards = targetGroup.leftCards.slice(0, realTargetCardIdx + 1);
        const remainingLeftCards = targetGroup.leftCards.slice(realTargetCardIdx + 1);

        const lastCutOffLeftCardKey = cutOffLeftCards[cutOffLeftCards.length - 1].key;
        const breakpoint = virtualRects[lastCutOffLeftCardKey].top + virtualRects[lastCutOffLeftCardKey].height;

        const firstNotFittingRightCardIdx = targetGroup.rightCards.findIndex((rightCard) => {
          const bottomPointOfCard = virtualRects[rightCard.key].top + virtualRects[rightCard.key].height;

          return bottomPointOfCard > breakpoint;
        });

        const cutOffRightCards =
          firstNotFittingRightCardIdx === -1 ? targetGroup.rightCards : targetGroup.rightCards.slice(0, firstNotFittingRightCardIdx);
        const remainingRightCards = firstNotFittingRightCardIdx === -1 ? [] : targetGroup.rightCards.slice(firstNotFittingRightCardIdx);

        newGroups.splice(
          targetGroupIdx,
          1,
          {
            type: MovableCardsContainerGroupTypeEnum.TwoColumns,
            leftCards: cutOffLeftCards,
            rightCards: cutOffRightCards,
          },
          newGroup,
          {
            type: MovableCardsContainerGroupTypeEnum.TwoColumns,
            leftCards: remainingLeftCards,
            rightCards: remainingRightCards,
          }
        );
      }
    } else {
      const targetCardColumnIsEmpty = targetGroup.rightCards.length === 0;
      if (targetCardColumnIsEmpty) {
        return groups;
      }

      const realTargetCardIdx = targetAreaCardIdx ?? targetGroup.rightCards.length - 1;

      if (realTargetCardIdx === targetGroup.rightCards.length - 1) {
        const breakpoint =
          virtualRects[targetGroup.rightCards[realTargetCardIdx].key].top +
          virtualRects[targetGroup.rightCards[realTargetCardIdx].key].height;

        const firstNotFittingLeftCardIdx = targetGroup.leftCards.findIndex((leftCard) => {
          const bottomPointOfCard = virtualRects[leftCard.key].top + virtualRects[leftCard.key].height;

          return bottomPointOfCard > breakpoint;
        });

        const cutOffLeftCards =
          firstNotFittingLeftCardIdx === -1 ? targetGroup.leftCards : targetGroup.leftCards.slice(0, firstNotFittingLeftCardIdx);
        const remainingLeftCards = firstNotFittingLeftCardIdx === -1 ? [] : targetGroup.leftCards.slice(firstNotFittingLeftCardIdx);

        newGroups.splice(
          targetGroupIdx,
          1,
          {
            type: MovableCardsContainerGroupTypeEnum.TwoColumns,
            leftCards: cutOffLeftCards,
            rightCards: targetGroup.rightCards,
          },
          newGroup,
          {
            type: MovableCardsContainerGroupTypeEnum.TwoColumns,
            leftCards: remainingLeftCards,
            rightCards: [],
          }
        );
      } else {
        const cutOffRightCards = targetGroup.rightCards.slice(0, realTargetCardIdx + 1);
        const remainingRightCards = targetGroup.rightCards.slice(realTargetCardIdx + 1);

        const lastCutOffRightCardKey = cutOffRightCards[cutOffRightCards.length - 1].key;
        const breakpoint = virtualRects[lastCutOffRightCardKey].top + virtualRects[lastCutOffRightCardKey].height;

        const firstNotFittingLeftCardIdx = targetGroup.leftCards.findIndex((leftCard) => {
          const bottomPointOfCard = virtualRects[leftCard.key].top + virtualRects[leftCard.key].height;

          return bottomPointOfCard > breakpoint;
        });

        const cutOffLeftCards =
          firstNotFittingLeftCardIdx === -1 ? targetGroup.leftCards : targetGroup.leftCards.slice(0, firstNotFittingLeftCardIdx);
        const remainingLeftCards = firstNotFittingLeftCardIdx === -1 ? [] : targetGroup.leftCards.slice(firstNotFittingLeftCardIdx);

        newGroups.splice(
          targetGroupIdx,
          1,
          {
            type: MovableCardsContainerGroupTypeEnum.TwoColumns,
            leftCards: cutOffLeftCards,
            rightCards: cutOffRightCards,
          },
          newGroup,
          {
            type: MovableCardsContainerGroupTypeEnum.TwoColumns,
            leftCards: remainingLeftCards,
            rightCards: remainingRightCards,
          }
        );
      }
    }

    return this.mergeConsecutiveGroups(newGroups);
  }

  private changeSingleColumnCardToTwoColumnsCard(
    groups: (MovableCardsContainerSingleColumnGroup<T> | MovableCardsContainerTwoColumnsGroup<T>)[],
    groupIdx: number,
    cardIdx: number
  ): (MovableCardsContainerSingleColumnGroup<T> | MovableCardsContainerTwoColumnsGroup<T>)[] {
    const group = groups[groupIdx] as MovableCardsContainerSingleColumnGroup<T>;
    const newGroups = [...groups];
    const newGroup: MovableCardsContainerTwoColumnsGroup<T> = {
      type: MovableCardsContainerGroupTypeEnum.TwoColumns,
      leftCards: [group.cards[cardIdx]],
      rightCards: [],
    };

    const cutOffCards = group.cards.slice(0, cardIdx);
    const remainingCards = group.cards.slice(cardIdx + 1);

    newGroups.splice(
      groupIdx,
      1,
      {
        type: MovableCardsContainerGroupTypeEnum.SingleColumn,
        cards: cutOffCards,
      },
      newGroup,
      {
        type: MovableCardsContainerGroupTypeEnum.SingleColumn,
        cards: remainingCards,
      }
    );

    return this.mergeConsecutiveGroups(newGroups);
  }

  private changeTwoColumnsCardToSingleColumnCard(
    groups: (MovableCardsContainerSingleColumnGroup<T> | MovableCardsContainerTwoColumnsGroup<T>)[],
    groupIdx: number,
    columnIdx: number,
    cardIdx: number,
    virtualRects: Record<string, SimpleDomRect>
  ): (MovableCardsContainerSingleColumnGroup<T> | MovableCardsContainerTwoColumnsGroup<T>)[] {
    const group = groups[groupIdx] as MovableCardsContainerTwoColumnsGroup<T>;
    const newGroups = [...groups];
    const newGroup: MovableCardsContainerSingleColumnGroup<T> = {
      type: MovableCardsContainerGroupTypeEnum.SingleColumn,
      cards: columnIdx === 0 ? [group.leftCards[cardIdx]] : [group.rightCards[cardIdx]],
    };

    if (columnIdx === 0) {
      const cutOffLeftCards = group.leftCards.slice(0, cardIdx);
      const remainingLeftCards = group.leftCards.slice(cardIdx + 1);

      const lastCutOffLeftCardKey = cutOffLeftCards[cutOffLeftCards.length - 1]?.key;

      const breakpoint = lastCutOffLeftCardKey ? virtualRects[lastCutOffLeftCardKey].top + virtualRects[lastCutOffLeftCardKey].height : 0;

      const firstNotFittingRightCardIdx = group.rightCards.findIndex((rightCard) => {
        const bottomPointOfCard = virtualRects[rightCard.key].top + virtualRects[rightCard.key].height;

        return bottomPointOfCard > breakpoint;
      });

      const cutOffRightCards =
        firstNotFittingRightCardIdx === -1 ? group.rightCards : group.rightCards.slice(0, firstNotFittingRightCardIdx);
      const remainingRightCards = firstNotFittingRightCardIdx === -1 ? [] : group.rightCards.slice(firstNotFittingRightCardIdx);

      newGroups.splice(
        groupIdx,
        1,
        {
          type: MovableCardsContainerGroupTypeEnum.TwoColumns,
          leftCards: cutOffLeftCards,
          rightCards: cutOffRightCards,
        },
        newGroup,
        {
          type: MovableCardsContainerGroupTypeEnum.TwoColumns,
          leftCards: remainingLeftCards,
          rightCards: remainingRightCards,
        }
      );
    } else {
      const cutOffRightCards = group.rightCards.slice(0, cardIdx);
      const remainingRightCards = group.rightCards.slice(cardIdx + 1);

      const lastCutOffRightCardKey = cutOffRightCards[cutOffRightCards.length - 1]?.key;

      const breakpoint = lastCutOffRightCardKey
        ? virtualRects[lastCutOffRightCardKey].top + virtualRects[lastCutOffRightCardKey].height
        : 0;

      const firstNotFittingLeftCardIdx = group.leftCards.findIndex((leftCard) => {
        const bottomPointOfCard = virtualRects[leftCard.key].top + virtualRects[leftCard.key].height;

        return bottomPointOfCard > breakpoint;
      });

      const cutOffLeftCards = firstNotFittingLeftCardIdx === -1 ? group.leftCards : group.leftCards.slice(0, firstNotFittingLeftCardIdx);
      const remainingLeftCards = firstNotFittingLeftCardIdx === -1 ? [] : group.leftCards.slice(firstNotFittingLeftCardIdx);

      newGroups.splice(
        groupIdx,
        1,
        {
          type: MovableCardsContainerGroupTypeEnum.TwoColumns,
          leftCards: cutOffLeftCards,
          rightCards: cutOffRightCards,
        },
        newGroup,
        {
          type: MovableCardsContainerGroupTypeEnum.TwoColumns,
          leftCards: remainingLeftCards,
          rightCards: remainingRightCards,
        }
      );
    }

    return this.mergeConsecutiveGroups(newGroups);
  }

  private mergeConsecutiveTwoColumnsGroups(
    targetGroup: MovableCardsContainerTwoColumnsGroup<T>,
    groupToMerge: MovableCardsContainerTwoColumnsGroup<T>
  ): MovableCardsContainerTwoColumnsGroup<T> {
    const newTargetGroup: MovableCardsContainerTwoColumnsGroup<T> = {
      ...targetGroup,
      leftCards: [...targetGroup.leftCards, ...groupToMerge.leftCards],
      rightCards: [...targetGroup.rightCards, ...groupToMerge.rightCards],
    };

    return newTargetGroup;
  }

  private isGroupEmpty(group: MovableCardsContainerSingleColumnGroup<T> | MovableCardsContainerTwoColumnsGroup<T>): boolean {
    return group.type === MovableCardsContainerGroupTypeEnum.SingleColumn
      ? group.cards.length === 0
      : group.leftCards.length === 0 && group.rightCards.length === 0;
  }
}
